import {
  DEFAULT_RADAR_CONSENT_TIMEOUT_MS,
  RADAR_CONSENT_REPORT_DELAY_MS,
} from "@/config/timeouts";
import { configManager } from "@/features/client-config/services/config-manager";
import { delay } from "@/features/radar/utils/delay/delay";
import { networkDebugMonitor } from "@/services/monitoring/network-debug-monitor/network-debug-monitor";
import { captureException, truncateForSentry } from "@/utils/sentry";
import { joinUrl } from "@/utils/url";

const CONSENT_ENDPOINT = "api/Consent";
const SERVICE_NAME = "radar-consent";

interface ConsentContext {
  radarId: string;
  shotId: string;
  consented: boolean;
  consentRequired: boolean;
}

interface RadarConnectionConfig {
  baseUrl: string;
  timeoutMs: number;
}

function captureConsentException(
  error: unknown,
  operation: string,
  context: ConsentContext,
  extra?: Record<string, unknown>
): void {
  captureException(error, {
    service: SERVICE_NAME,
    operation,
    radarId: context.radarId,
    shotId: context.shotId,
    consentValue: context.consented,
    consentRequired: context.consentRequired,
    ...extra,
  });
}

/**
 * Radar Consent Service
 *
 * Reports whether a shot was consented to via POST /api/Consent. Call on
 * final measurement — that is when Radar waits for the consent answer.
 * Waits `RADAR_CONSENT_REPORT_DELAY_MS` after final measurement before
 * posting, since Radar expects consent slightly after the final
 * measurement rather than immediately. Deduped per shot id, though a
 * retry is allowed if config/base URL resolution fails so the report
 * isn't permanently lost.
 */
const MAX_TRACKED_SHOT_IDS = 200;

class RadarConsentService {
  private reportedShotIds = new Set<string>();
  /** Abort controllers for pending report delays, keyed by shot id */
  private pendingReportDelayControllers = new Map<string, AbortController>();

  /**
   * Reports consent for a shot. No-op for shot ids already reported.
   * Fire-and-forget: never throws, failures go to Sentry.
   */
  async reportConsent(
    radarId: string,
    shotId: string,
    consented: boolean,
    consentRequired: boolean
  ): Promise<void> {
    if (this.reportedShotIds.has(shotId)) {
      return;
    }
    this.trackShotId(shotId);

    const context: ConsentContext = {
      radarId,
      shotId,
      consented,
      consentRequired,
    };

    const connectionConfig = await this.resolveConnectionConfig(context);
    if (!connectionConfig) {
      this.reportedShotIds.delete(shotId);
      return;
    }

    const shouldPost = await this.waitForReportDelay(shotId);
    if (!shouldPost) {
      this.reportedShotIds.delete(shotId);
      return;
    }

    await this.postConsent(connectionConfig, context);
  }

  /**
   * Cancel all pending consent-report delays (clears timers).
   * Call on session clear to avoid leaking timers across sessions.
   */
  clearPendingReportDelays(): void {
    for (const controller of this.pendingReportDelayControllers.values()) {
      controller.abort();
    }
    this.pendingReportDelayControllers.clear();
  }

  private trackShotId(shotId: string): void {
    this.reportedShotIds.add(shotId);
    if (this.reportedShotIds.size > MAX_TRACKED_SHOT_IDS) {
      const oldestShotId = this.reportedShotIds.values().next().value;
      if (oldestShotId !== undefined) {
        this.reportedShotIds.delete(oldestShotId);
      }
    }
  }

  private async resolveConnectionConfig(
    context: ConsentContext
  ): Promise<RadarConnectionConfig | null> {
    try {
      const config = await configManager.getConfig();
      const radar = config?.radars.find((r) => r.id === context.radarId);
      const baseUrl = radar?.radarHttpBaseUrl?.trim() || null;
      const timeoutMs =
        config?.timeoutConfig?.radarConsentTimeoutMs ??
        DEFAULT_RADAR_CONSENT_TIMEOUT_MS;

      if (!baseUrl) {
        return null;
      }

      return { baseUrl, timeoutMs };
    } catch (error) {
      captureConsentException(error, "get-config", context);
      return null;
    }
  }

  /**
   * Wait `RADAR_CONSENT_REPORT_DELAY_MS` before the consent POST, with
   * abortable cleanup. Returns false if the delay was cancelled.
   */
  private async waitForReportDelay(shotId: string): Promise<boolean> {
    this.cancelReportDelay(shotId);

    const controller = new AbortController();
    this.pendingReportDelayControllers.set(shotId, controller);

    try {
      await delay(RADAR_CONSENT_REPORT_DELAY_MS, controller.signal);
      return !controller.signal.aborted;
    } finally {
      if (this.pendingReportDelayControllers.get(shotId) === controller) {
        this.pendingReportDelayControllers.delete(shotId);
      }
    }
  }

  private cancelReportDelay(shotId: string): void {
    const controller = this.pendingReportDelayControllers.get(shotId);
    if (!controller) {
      return;
    }
    controller.abort();
    this.pendingReportDelayControllers.delete(shotId);
  }

  private async postConsent(
    connectionConfig: RadarConnectionConfig,
    context: ConsentContext
  ): Promise<void> {
    const url = joinUrl(connectionConfig.baseUrl, CONSENT_ENDPOINT);
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      connectionConfig.timeoutMs
    );
    const startedAt = performance.now();
    const requestBody = JSON.stringify({ value: context.consented });

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: requestBody,
        signal: controller.signal,
      });

      let responseBody = "";
      if (networkDebugMonitor.isActive() || !response.ok) {
        try {
          responseBody = await response.text();
        } catch {
          // Response body is best-effort context for Sentry/debug console only.
        }
      }

      networkDebugMonitor.logHttpRequest({
        service: SERVICE_NAME,
        method: "POST",
        url,
        status: response.status,
        durationMs: performance.now() - startedAt,
        requestBody,
        responseBody,
        radarId: context.radarId,
      });

      if (!response.ok) {
        captureConsentException(
          new Error(`Radar consent POST failed: HTTP ${response.status}`),
          "report",
          context,
          {
            status: response.status,
            responseBody: truncateForSentry(responseBody),
            endpoint: url,
          }
        );
      }
    } catch (error) {
      networkDebugMonitor.logHttpRequest({
        service: SERVICE_NAME,
        method: "POST",
        url,
        durationMs: performance.now() - startedAt,
        error: error instanceof Error ? error.message : String(error),
        requestBody,
        radarId: context.radarId,
      });
      captureConsentException(error, "report", context, { endpoint: url });
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

export const radarConsentService = new RadarConsentService();
